// Screen A - the Certify dashboard (docs/design/slice4-certify-ux.md §4).
// Route: /apps/:appId/certify. Three independent sections (§4.1/§4.2): scanners,
// framework self-assessment (always-on GDPR + opt-in), builder health - each its own
// `.card.pad` so one section's error/empty state never blanks the others (§4.3).
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { Assessment, BuilderHealthCheck, FrameworkKey, HealthStatus, ScanResult } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import {
  confirmGdprRoutingItem,
  getBuilderHealth,
  getCertifySummary,
  needsFounderDecision,
  runAssessment,
  runScan,
} from "../../api/certify.js";
import type { CertifySummaryResponse } from "../../api/certify.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { evaluateCertifyGate } from "../../certify/gate.js";
import { certifyCopy, type CertifyCopyKey } from "../../copy/certify.js";
import { CertifyTierRefusalCard } from "../../components/CertifyTierRefusalCard.js";
import { LifecycleGateCard } from "../../components/LifecycleGateCard.js";
import { FrameworkResultCard } from "../../components/FrameworkResultCard.js";
import { GdprRoutingBanner, GdprRoutingPanel } from "../../components/GdprRoutingPanel.js";

const FRAMEWORK_LABELS: Record<FrameworkKey, string> = {
  gdpr: "GDPR",
  soc2: "SOC 2",
  iso27001: "ISO 27001",
  nis2: "NIS2",
  hipaa: "HIPAA",
};
const OPTIONAL_FRAMEWORKS: readonly FrameworkKey[] = ["soc2", "iso27001", "nis2", "hipaa"];

type SummaryState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "loaded"; readonly summary: CertifySummaryResponse };

type HealthState =
  | { readonly status: "loading" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly health: BuilderHealthCheck };

/** The scanner/framework section's own combined state, folding `summaryState` +
 * `runOverride` (a local, this-session run result the PROPOSED aggregate GET may not
 * yet reflect, §9.9) + `running` into the one branch the ux spec's §4.3 states table
 * names - Error and Empty are DISTINCT here (a fetch failure never silently renders as
 * "never run"). */
type SectionStatus = "loading" | "error" | "empty" | "ready" | "scanning";

const HEALTH_STATUS_CHIP: Record<HealthStatus, string> = {
  ok: "status-chip good",
  warning: "status-chip warn",
  fail: "status-chip risk",
  unknown: "status-chip neutral",
};

// W1 fix (design-review): rows previously printed the raw lowercase `HealthStatus` enum
// value ("ok"/"warning"/"fail") verbatim instead of copy - an internal wire token
// leaking to a founder-facing row. Exhaustive over the closed `HealthStatus` enum
// (packages/shared/src/model/certify.ts:206) - no fallback branch needed, since a value
// outside the enum fails to parse at the boundary (app-engineering, make illegal states
// unrepresentable), so this map can never miss a case silently.
const HEALTH_STATUS_LABEL_KEY: Record<HealthStatus, CertifyCopyKey> = {
  ok: "certify.dashboard.builderHealth.status.ok",
  warning: "certify.dashboard.builderHealth.status.warning",
  fail: "certify.dashboard.builderHealth.status.fail",
  unknown: "certify.dashboard.builderHealth.notYetChecked",
};

const HealthRow = ({
  label,
  status,
}: {
  readonly label: string;
  readonly status: HealthStatus;
}): React.JSX.Element => (
  <div className="fp-row">
    <span>{label}</span>
    <span className={HEALTH_STATUS_CHIP[status]}>{certifyCopy(HEALTH_STATUS_LABEL_KEY[status])}</span>
  </div>
);

// W3 fix (design-review): a section's error state previously rendered ONLY the
// reassurance body ("Nothing changed and nothing was skipped.") with no statement of
// what failed. Leads with the generic error title (certify.ts:225, already defined and
// unused until now) before the body. One shared component (Rule of Three - this is the
// third identical block: scanners/frameworks/health each had their own copy) so all
// three sections stay consistent by construction.
const SectionError = ({ onRetry }: { readonly onRetry: () => void }): React.JSX.Element => (
  <div className="mt12">
    <p style={{ fontWeight: 600 }}>{certifyCopy("certify.errors.generic.title")}</p>
    <p className="muted mt8">{certifyCopy("certify.errors.generic.body")}</p>
    <button type="button" className="btn btn-ghost btn-sm mt8" onClick={onRetry}>
      {certifyCopy("certify.errors.generic.retryCta")}
    </button>
  </div>
);

const ScannerRow = ({ scan }: { readonly scan: ScanResult }): React.JSX.Element => {
  let chipClass = "status-chip good";
  let tagText = certifyCopy("certify.dashboard.scanners.tag.pass");
  if (scan.status === "error") {
    chipClass = "status-chip neutral";
    tagText = certifyCopy("certify.dashboard.scanners.tag.error");
  } else if (scan.status === "fail") {
    chipClass = "status-chip risk";
    tagText =
      scan.counts.failed > 0
        ? certifyCopy("certify.dashboard.scanners.tag.actionNeeded", { count: scan.counts.failed })
        : certifyCopy("certify.dashboard.scanners.tag.fail");
  }
  return (
    <div className="fp-row" data-testid="scanner-row" data-status={scan.status}>
      <div>
        <b style={{ fontSize: 13.5 }}>{scan.scannerKey}</b>
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {scan.counts.passed} of {scan.counts.total} passed
        </p>
      </div>
      <span className={chipClass}>{tagText}</span>
    </div>
  );
};

export const CertifyDashboard = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const navigate = useNavigate();
  const { state: session } = useSession();
  const account = session.status === "ready" ? session.account : null;
  const canManage = isOwnerOrAdmin(session);
  const gate = evaluateCertifyGate(app, account);

  const [summaryState, setSummaryState] = useState<SummaryState>({ status: "loading" });
  const [healthState, setHealthState] = useState<HealthState>({ status: "loading" });
  const [runOverride, setRunOverride] = useState<{
    readonly scans: readonly ScanResult[];
    readonly assessments: readonly Assessment[];
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selectedFrameworks, setSelectedFrameworks] = useState<readonly FrameworkKey[]>([]);
  const [routingPanelOpen, setRoutingPanelOpen] = useState(false);
  const [summaryAttempt, setSummaryAttempt] = useState(0);
  const [healthAttempt, setHealthAttempt] = useState(0);
  const routingOpenButtonRef = useRef<HTMLButtonElement>(null);

  const entitled = !gate.tierDenied && !gate.stageDenied;

  useEffect(() => {
    if (!entitled) return;
    const cancelled = { current: false };
    setSummaryState({ status: "loading" });
    void (async () => {
      const result = await getCertifySummary(appId);
      if (cancelled.current) return;
      setSummaryState(isErr(result) ? { status: "error" } : { status: "loaded", summary: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [entitled, appId, summaryAttempt]);

  useEffect(() => {
    if (!entitled) return;
    const cancelled = { current: false };
    setHealthState({ status: "loading" });
    void (async () => {
      const result = await getBuilderHealth(appId);
      if (cancelled.current) return;
      setHealthState(isErr(result) ? { status: "error" } : { status: "ready", health: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [entitled, appId, healthAttempt]);

  if (gate.tierDenied) {
    return (
      <div className="wrap">
        <CertifyTierRefusalCard
          appName={app.name}
          stageDenied={gate.stageDenied}
          onAskAboutUpgrading={() => undefined}
        />
      </div>
    );
  }
  if (gate.stageDenied) {
    return (
      <div className="wrap">
        {/* Closes the stub named at LifecycleGateCard.tsx's own header comment (Slice 5,
            docs/phase-0/slice5-ux-flows.md §2.1) - the real Build destination now
            exists (`/apps/:appId/build`, AppBuildTab.tsx). */}
        <LifecycleGateCard
          appName={app.name}
          onGoToBuild={() => {
            void navigate(`/apps/${appId}/build`);
          }}
        />
      </div>
    );
  }

  const sectionStatus: SectionStatus = running
    ? "scanning"
    : runOverride !== null
      ? "ready"
      : summaryState.status === "loading"
        ? "loading"
        : summaryState.status === "error"
          ? "error"
          : summaryState.summary.hasRunBefore
            ? "ready"
            : "empty";

  const scans = runOverride?.scans ?? (summaryState.status === "loaded" ? summaryState.summary.scans : []);
  const assessments =
    runOverride?.assessments ?? (summaryState.status === "loaded" ? summaryState.summary.assessments : []);
  const gdprAssessment = assessments.find((a) => a.framework === "gdpr");
  const routingItems = (gdprAssessment?.controls ?? [])
    .filter(needsFounderDecision)
    .map((c) => ({ controlKey: c.control.controlKey, statement: c.control.statement }));

  const startRun = (frameworks: readonly FrameworkKey[]): void => {
    setPickerOpen(false);
    setRunning(true);
    void (async () => {
      const scanResult = await runScan(appId);
      const allFrameworks: readonly FrameworkKey[] = ["gdpr", ...frameworks];
      const assessResults = await Promise.all(allFrameworks.map((f) => runAssessment(appId, f)));
      setRunning(false);
      if (isErr(scanResult) || assessResults.some(isErr)) return; // section keeps its prior state; a real error surfaces via retry
      const newAssessments = assessResults.filter((r) => !isErr(r)).map((r) => r.value);
      setRunOverride({ scans: scanResult.value.results, assessments: newAssessments });
      setSummaryAttempt((a) => a + 1); // best-effort summary refresh (the "at a glance" badge)
    })();
  };

  const handleReassess = (framework: FrameworkKey): void => {
    setRunning(true);
    void (async () => {
      const result = await runAssessment(appId, framework);
      setRunning(false);
      if (isErr(result)) return;
      const base = assessments.filter((a) => a.framework !== framework);
      setRunOverride({ scans, assessments: [...base, result.value] });
    })();
  };

  const handleConfirmRouting = (controlKey: string): void => {
    void (async () => {
      const result = await confirmGdprRoutingItem(appId, controlKey);
      if (isErr(result)) return;
      const remaining = routingItems.filter((i) => i.controlKey !== controlKey);
      if (remaining.length === 0) setRoutingPanelOpen(false);
    })();
  };

  return (
    <div className="wrap">
      <h1 className="h2">{certifyCopy("certify.dashboard.title")}</h1>
      <p className="muted mt8">{certifyCopy("certify.dashboard.subtitle", { appName: app.name })}</p>

      <GdprRoutingBanner
        count={routingItems.length}
        canAct={canManage}
        onOpen={() => {
          setRoutingPanelOpen(true);
        }}
      />
      {routingPanelOpen ? (
        <GdprRoutingPanel
          items={routingItems}
          onConfirm={handleConfirmRouting}
          onClose={() => {
            setRoutingPanelOpen(false);
          }}
          triggerRef={routingOpenButtonRef}
        />
      ) : null}

      <div className="certify-dashboard-grid mt16">
        <div className="stack gap16">
          {/* ---- Scanners ---- */}
          <section className="card pad" aria-labelledby="certify-scanners-heading" data-testid="section-scanners">
            <h2 className="h3" id="certify-scanners-heading">
              {certifyCopy("certify.dashboard.scanners.heading")}
            </h2>
            {sectionStatus === "loading" ? (
              <div className="stack gap8 mt12">
                <div className="skeleton skeleton-line" />
                <div className="skeleton skeleton-line" />
              </div>
            ) : sectionStatus === "error" ? (
              <SectionError
                onRetry={() => {
                  setSummaryAttempt((a) => a + 1);
                }}
              />
            ) : sectionStatus === "scanning" ? (
              <div className="mt12" data-testid="scanning-progress">
                <div className="meter bordered">
                  <span style={{ width: "60%" }} />
                </div>
                <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
                  {certifyCopy("certify.dashboard.scanning.body", { appName: app.name })}
                </p>
              </div>
            ) : sectionStatus === "empty" ? (
              <p className="muted mt8">Not run yet.</p>
            ) : (
              <div className="mt8">
                {scans.map((scan, i) => (
                  <ScannerRow key={`${scan.scannerKey}-${String(i)}`} scan={scan} />
                ))}
              </div>
            )}
          </section>

          {/* ---- Framework self-assessment ---- */}
          <section className="card pad" aria-labelledby="certify-frameworks-heading" data-testid="section-frameworks">
            <h2 className="h3" id="certify-frameworks-heading">
              {certifyCopy("certify.dashboard.section.frameworks")}
            </h2>

            {sectionStatus === "loading" ? (
              <div className="stack gap8 mt12">
                <div className="skeleton skeleton-card" />
              </div>
            ) : sectionStatus === "error" ? (
              <SectionError
                onRetry={() => {
                  setSummaryAttempt((a) => a + 1);
                }}
              />
            ) : null}

            {sectionStatus === "empty" ? (
              <div className="card state-card mt12" data-testid="certify-empty-never-run">
                <div className="glim-badge" aria-hidden="true" />
                <p className="muted">{certifyCopy("certify.dashboard.emptyNeverRun.heading", { appName: app.name })}</p>
                <p className="muted" style={{ fontSize: 12.5 }}>
                  {certifyCopy("certify.dashboard.emptyNeverRun.body")}
                </p>
                {canManage ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setPickerOpen(true);
                    }}
                  >
                    {certifyCopy("certify.dashboard.emptyNeverRun.cta")}
                  </button>
                ) : null}
              </div>
            ) : null}

            {pickerOpen ? (
              <div className="well pad mt12" data-testid="framework-picker" role="group" aria-label="Choose frameworks">
                <p style={{ fontSize: 13, fontWeight: 600 }}>{certifyCopy("certify.dashboard.frameworks.heading")}</p>
                <p className="muted mt8" style={{ fontSize: 12.5 }}>
                  {certifyCopy("certify.dashboard.frameworks.subtitle", { appName: app.name })}
                </p>
                <div className="stack gap8 mt12">
                  <label className="row aic gap8">
                    <input type="checkbox" checked disabled />
                    GDPR <span className="chip">{certifyCopy("certify.dashboard.frameworks.alwaysIncluded")}</span>
                  </label>
                  {OPTIONAL_FRAMEWORKS.map((fw) => (
                    <label key={fw} className="row aic gap8">
                      <input
                        type="checkbox"
                        checked={selectedFrameworks.includes(fw)}
                        onChange={(e) => {
                          setSelectedFrameworks((prev) =>
                            e.target.checked ? [...prev, fw] : prev.filter((f) => f !== fw),
                          );
                        }}
                      />
                      {FRAMEWORK_LABELS[fw]}
                    </label>
                  ))}
                </div>
                <p className="muted mt8" style={{ fontSize: 11.5 }}>
                  {certifyCopy("certify.dashboard.frameworks.disclaimerInline")}
                </p>
                <div className="actions mt12">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      startRun(selectedFrameworks);
                    }}
                  >
                    {certifyCopy("certify.dashboard.emptyNeverRun.cta")}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => {
                      setPickerOpen(false);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : null}

            {sectionStatus === "scanning" ? (
              <div className="mt12" data-testid="scanning-progress-frameworks">
                <div className="meter bordered">
                  <span style={{ width: "60%" }} />
                </div>
                <p className="muted mt8" role="status" style={{ fontSize: 12.5 }}>
                  {certifyCopy("certify.dashboard.scanning.body", { appName: app.name })}
                </p>
              </div>
            ) : null}

            {sectionStatus === "ready" ? (
              <div className="stack gap16 mt12">
                {assessments.map((assessment) => (
                  <FrameworkResultCard
                    key={assessment.framework}
                    assessment={assessment}
                    appName={app.name}
                    // Conditional spread (not `onReassess={cond ? fn : undefined}`): under
                    // `exactOptionalPropertyTypes`, an optional prop may be OMITTED but
                    // never explicitly set to `undefined` - the prop is either present
                    // (owner/admin) or absent entirely (viewer), never "present as undefined".
                    {...(canManage
                      ? { onReassess: () => { handleReassess(assessment.framework); } }
                      : {})}
                  />
                ))}
              </div>
            ) : null}
          </section>

          {/* ---- Builder health ---- */}
          <section className="card pad" aria-labelledby="certify-health-heading" data-testid="section-health">
            <h2 className="h3" id="certify-health-heading">
              {certifyCopy("certify.dashboard.builderHealth.heading")}
            </h2>
            <p className="muted mt8" style={{ fontSize: 12.5 }}>
              {certifyCopy("certify.dashboard.builderHealth.subtitle")}
            </p>
            {healthState.status === "loading" ? (
              <div className="stack gap8 mt12">
                <div className="skeleton skeleton-line" />
                <div className="skeleton skeleton-line" />
              </div>
            ) : healthState.status === "error" ? (
              <SectionError
                onRetry={() => {
                  setHealthAttempt((a) => a + 1);
                }}
              />
            ) : (
              <div className="mt8">
                <HealthRow label="SPF" status={healthState.health.emailDeliverability.spf} />
                <HealthRow label="DKIM" status={healthState.health.emailDeliverability.dkim} />
                <HealthRow label="DMARC" status={healthState.health.emailDeliverability.dmarc} />
                <HealthRow label="MX" status={healthState.health.emailDeliverability.mx} />
                <HealthRow
                  label={certifyCopy("certify.dashboard.builderHealth.domainSsl.name")}
                  status={healthState.health.domainSsl.valid ? "ok" : "fail"}
                />
                <HealthRow
                  label={certifyCopy("certify.dashboard.builderHealth.payments.name")}
                  status={healthState.health.payments.status}
                />
              </div>
            )}
          </section>
        </div>

        {/* ---- At a glance ---- */}
        <aside className="card pad" data-testid="certify-at-a-glance">
          <h2 className="h3">{certifyCopy("certify.dashboard.atAGlance.heading")}</h2>
          <p className="mt8" style={{ fontWeight: 600 }}>
            {sectionStatus === "scanning"
              ? certifyCopy("certify.dashboard.statusChip.assessing")
              : summaryState.status === "loaded" && summaryState.summary.badge === "certified_current"
                ? certifyCopy("certify.dashboard.statusChip.certified", { validUntilDate: "-" })
                : summaryState.status === "loaded" && summaryState.summary.badge === "dulled"
                  ? certifyCopy("certify.dashboard.statusChip.dulled", { failingCount: 1 })
                  : certifyCopy("certify.dashboard.statusChip.notYetCertified")}
          </p>
          <p className="muted mt8" style={{ fontSize: 12 }}>
            {sectionStatus === "ready" || sectionStatus === "scanning"
              ? certifyCopy("certify.dashboard.atAGlance.lastRun", {
                  date:
                    summaryState.status === "loaded" && summaryState.summary.lastRunAt !== null
                      ? new Date(summaryState.summary.lastRunAt).toLocaleDateString()
                      : new Date().toLocaleDateString(),
                })
              : certifyCopy("certify.dashboard.atAGlance.neverRun")}
          </p>
          <div className="stack gap8 mt16">
            <Link className="btn btn-ghost btn-sm" to={`/apps/${appId}/certify/data-room`}>
              {certifyCopy("certify.dashboard.dataRoomLinkCta")}
            </Link>
            <Link className="btn btn-ghost btn-sm" to={`/apps/${appId}/certify/trust-page`}>
              {certifyCopy("certify.dashboard.trustPageLinkCta")}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
};
